(() => {
  'use strict';
  if (window.FultonsCleanVideo) return;
  const labels = window.FultonsVideoLabels || {play: 'Play video', pause: 'Pause video'};
  const states = new Map(), deferred = new WeakSet();
  const motion = matchMedia('(prefers-reduced-motion: reduce)'), connection = navigator.connection;
  const canAutoplay = () => !motion.matches && !connection?.saveData;
  const unobstructed = video => !document.hidden && !video.closest('[hidden]') && !Array.from(document.querySelectorAll('dialog[open]')).some(dialog => !dialog.contains(video));
  const eligible = state => state.video.isConnected && state.inView && unobstructed(state.video);
  function pause(state) {
    if (!state.video.paused) { state.automaticPause = true; state.video.pause(); }
  }
  function update(state) {
    const {video, button} = state;
    button.hidden = false;
    button.classList.toggle('fultons-video-toggle--clean', Boolean(state.played && !state.blocked));
    button.textContent = state.played && !state.blocked ? '' : '▷';
    button.setAttribute('aria-label', (video.paused ? labels.play : labels.pause) + (state.title ? ' — ' + state.title : ''));
  }
  function fit(state) {
    const {video, button} = state;
    Object.assign(button.style, {left: video.offsetLeft + 'px', top: video.offsetTop + 'px', width: video.offsetWidth + 'px', height: video.offsetHeight + 'px'});
  }
  function play(state, manual = false) {
    if (!unobstructed(state.video) || !manual && (!eligible(state) || !canAutoplay() || state.userPaused || state.blocked || state.completed)) return;
    if (manual) { state.userPaused = false; state.blocked = false; state.completed = false; state.manual = true; }
    if (!state.video.paused || state.pending) return;
    if (state.video.ended) state.video.currentTime = 0;
    state.pending = true;
    Promise.resolve(state.video.play()).catch(error => {
      if (error.name !== 'AbortError') { state.blocked = true; update(state); }
    }).finally(() => { state.pending = false; if (!state.owned && !eligible(state)) pause(state); });
  }
  function sync(state) {
    if (state.owned) return;
    if (!eligible(state) || !canAutoplay() && !state.manual) { pause(state); if (!state.inView) state.completed = false; }
    else play(state);
  }
  function mount(video) {
    if (states.has(video) || video.hasAttribute('data-gallery-inline-short-video') || video.closest('button,a')) return;
    const parent = video.parentElement;
    if (!parent) return;
    video.controls = false; video.removeAttribute('controls'); video.removeAttribute('autoplay');
    video.muted = true; video.defaultMuted = true; video.playsInline = true; video.setAttribute('playsinline', ''); video.setAttribute('muted', ''); video.tabIndex = -1;
    if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
    const button = document.createElement('button'); button.type = 'button'; button.className = 'fultons-video-toggle';
    parent.append(button);
    const owned = Boolean(video.closest('fultons-product-film,fultons-studio-film'));
    const state = {video, button, owned, title: video.getAttribute('aria-label') || video.title || '', played: !video.paused, inView: false};
    states.set(video, state);
    button.addEventListener('click', event => {
      event.stopPropagation();
      video.dispatchEvent(new CustomEvent('fultons:video-manual', {bubbles: true}));
      if (!video.paused && !video.ended) { state.automaticPause = false; video.pause(); }
      else play(state, true);
    });
    video.addEventListener('playing', () => {
      state.played = true; state.blocked = false; state.userPaused = false; update(state);
      if (!state.owned && !eligible(state)) pause(state);
    });
    video.addEventListener('pause', () => {
      if (!state.automaticPause && !video.ended) state.userPaused = true;
      state.automaticPause = false; update(state);
    });
    video.addEventListener('ended', () => { state.completed = true; update(state); });
    video.addEventListener('error', () => { state.blocked = true; update(state); });
    video.addEventListener('loadedmetadata', () => fit(state));
    state.resize = new ResizeObserver(() => fit(state)); state.resize.observe(video); state.resize.observe(parent);
    if (!owned && 'IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(entries => { const entry = entries[0]; state.inView = entry.isIntersecting && entry.intersectionRatio >= .6; sync(state); }, {threshold:[0,.6]});
      state.observer.observe(video);
    }
    fit(state); update(state);
  }
  const deferredObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting && entry.intersectionRatio >= .6 && canAutoplay() && unobstructed(entry.target)) {
      const root = entry.target;
      if (typeof root.loadContent === 'function') root.loadContent(false);
    }
  }, {threshold:[0,.6]}) : null;
  function scan(root = document) {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.matches?.('video')) mount(root);
    root.querySelectorAll('video').forEach(mount);
    const lazy = root.matches?.('deferred-media') ? [root] : [];
    lazy.push(...root.querySelectorAll('deferred-media'));
    for (const item of lazy) if (!deferred.has(item) && item.querySelector('template')?.content.querySelector('video')) { deferred.add(item); deferredObserver?.observe(item); }
  }
  const syncAll = () => states.forEach(state => sync(state));
  new MutationObserver(records => {
    for (const record of records) if (record.type === 'childList') record.addedNodes.forEach(scan);
    states.forEach((state, video) => { if (!video.isConnected) { state.resize.disconnect(); state.observer?.disconnect(); state.button.remove(); states.delete(video); } });
    syncAll();
  }).observe(document.documentElement, {subtree:true, childList:true, attributes:true, attributeFilter:['open','hidden']});
  document.addEventListener('visibilitychange', syncAll); motion.addEventListener('change', syncAll); connection?.addEventListener?.('change', syncAll);
  window.FultonsCleanVideo = {mount, scan}; scan();
})();
